/**
 * DB-aware wrappers for:
 * - fetching WP posts with full content analysis (→ wp_posts rows)
 * - per-post quality assessment (→ quality_* columns)
 * - per-map wrapper (load map → website_id → sync + assess)
 *
 * Wewnatrz korzysta z:
 *  - wpSyncService (fetchAllWpPosts, extract*) — stateless HTTP layer
 *  - wpHtmlAnalyzer.analyzeWpHtml — strukturalne metryki
 *  - wpQualityAssessor.assessWpPostQuality — scoring
 */

import { supabase } from "@/integrations/supabase/client";
import {
  fetchAllWpPosts,
  extractTerms,
  extractFeaturedImage,
  extractKeyword,
  computeContentHash,
  stripHtml,
  type WpSyncCredentials,
} from "./wpSyncService";
import { analyzeWpHtml } from "./wpHtmlAnalyzer";
import { assessWpPostQuality } from "./wpQualityAssessor";
import { logPublic, logDebug, logError } from "./contentEngine/contentLogger";
import { normalizeToTokens } from "@/utils/textNormalization";

const PREFIX = "[WP-SYNC]";

interface WebsiteCreds {
  id: string;
  name: string;
  url: string;
  login: string | null;
  password: string | null;
  api_key: string | null;
}

async function loadWebsite(websiteId: string): Promise<WebsiteCreds | null> {
  const { data, error } = await supabase
    .from("websites")
    .select("id, name, url, login, password, api_key")
    .eq("id", websiteId)
    .maybeSingle();
  if (error || !data) return null;
  return data as WebsiteCreds;
}

function credsFromWebsite(w: WebsiteCreds): WpSyncCredentials | null {
  const username = w.login;
  const password = w.api_key || w.password;
  if (!username || username === "EMPTY" || !password || password === "EMPTY") return null;
  return { url: w.url, username, password };
}

async function countQualityRecommendation(websiteId: string) {
  const rows = await supabase
    .from("wp_posts")
    .select("quality_recommendation", { count: "exact" })
    .eq("website_id", websiteId);
  if (rows.error || !rows.data) return { keep: 0, borderline: 0, refurbish: 0 };
  const breakdown = { keep: 0, borderline: 0, refurbish: 0 };
  for (const r of rows.data as Array<{ quality_recommendation?: string | null }>) {
    if (r.quality_recommendation === "keep") breakdown.keep++;
    else if (r.quality_recommendation === "borderline") breakdown.borderline++;
    else if (r.quality_recommendation === "refurbish") breakdown.refurbish++;
  }
  return breakdown;
}

// =============================================
// syncWpPostsWithContent
// =============================================

export type SyncLogType = "info" | "success" | "error" | "stage";
export type SyncOnLog = (type: SyncLogType, message: string, debug?: Record<string, unknown>) => void;

export interface SyncResult {
  fetched: number;
  updated: number;
  errors: Array<{ postId: number; error: string }>;
}

export async function syncWpPostsWithContent(
  websiteId: string,
  options: {
    userId: string;
    onProgress?: (current: number, total: number) => void;
    onLog?: SyncOnLog;
    forceRefetch?: boolean;
  },
): Promise<SyncResult> {
  const result: SyncResult = { fetched: 0, updated: 0, errors: [] };
  const website = await loadWebsite(websiteId);
  if (!website) {
    logError(PREFIX, `Website ${websiteId} nie istnieje`);
    throw new Error("Website not found");
  }
  const creds = credsFromWebsite(website);
  if (!creds) {
    logError(PREFIX, `Website ${website.name}: brak credentials`);
    throw new Error("Website credentials missing");
  }

  logPublic(PREFIX, `Start sync dla ${website.name} (${website.url})`);
  options.onLog?.("info", `  → Łączenie z WordPress REST API: ${website.url}`);

  const rawPosts = await fetchAllWpPosts(creds, (fetched, total) => {
    result.fetched = fetched;
    options.onProgress?.(fetched, total);
    // Log co ~50 fetched
    if (fetched > 0 && fetched % 50 === 0) {
      options.onLog?.("info", `  → Pobrano ${fetched}/${total} wpisów...`);
    }
  });

  logDebug(PREFIX, `Fetched ${rawPosts.length} postow, start upsert + analiza`);
  options.onLog?.("stage", `  ✓ Pobrano ${rawPosts.length} wpisów z WP REST API`);
  options.onLog?.("info", `  → Analiza HTML (wpHtmlAnalyzer) + UPSERT do wp_posts...`);

  // Build existing modified map (dla idempotentnosci)
  let existingByWpId: Map<number, string> = new Map();
  if (!options.forceRefetch) {
    const { data: existing } = await supabase
      .from("wp_posts")
      .select("wp_post_id, modified_at")
      .eq("website_id", websiteId);
    if (existing) {
      existingByWpId = new Map(
        (existing as Array<{ wp_post_id: number; modified_at: string | null }>)
          .map((r) => [r.wp_post_id, r.modified_at || ""]),
      );
    }
  }

  for (const rp of rawPosts) {
    try {
      // Skip jesli modified nie zmieniony (idempotency)
      if (!options.forceRefetch) {
        const prevMod = existingByWpId.get(rp.id);
        if (prevMod && prevMod === rp.modified) {
          continue;
        }
      }

      const categories = extractTerms(rp, "category");
      const tags = extractTerms(rp, "post_tag");
      const featuredImage = extractFeaturedImage(rp);
      const keyword = extractKeyword(rp);
      const content = rp.content.rendered;
      const excerpt = stripHtml(rp.excerpt.rendered).substring(0, 500);
      const contentHash = await computeContentHash(content);

      // Analiza strukturalna (+ plaintext)
      const metrics = analyzeWpHtml(content, website.url);

      // M8: first_100_words — pierwsze 100 slow z plaintext (free, bez LLM).
      // Przydatne do SRO extraction + preview w UI.
      const plainTokens = metrics.plaintext.split(/\s+/).filter((w) => w.length > 0);
      const first100Words = plainTokens.slice(0, 100).join(" ");

      // M9: pre-computed tokens (normalizacja tokens z stopwords + diacritics strip)
      // uzywane w matcher Level 2 asymmetric containment.
      const titleTokens = normalizeToTokens(stripHtml(rp.title.rendered));
      const first100Tokens = normalizeToTokens(first100Words);

      const postData = {
        user_id: options.userId,
        website_id: websiteId,
        wp_post_id: rp.id,
        title: stripHtml(rp.title.rendered),
        slug: rp.slug,
        status: rp.status,
        excerpt,
        content,
        content_hash: contentHash,
        keyword: keyword || null,
        published_at: rp.date,
        modified_at: rp.modified,
        synced_at: new Date().toISOString(),
        categories: categories as unknown as Record<string, unknown>[],
        tags: tags as unknown as Record<string, unknown>[],
        featured_image_url: featuredImage,
        wp_url: rp.link,
        // M2 extensions
        content_plaintext: metrics.plaintext.slice(0, 100_000), // bezpieczny cap
        word_count: metrics.word_count,
        h2_count: metrics.h2_count,
        h3_count: metrics.h3_count,
        outbound_links_count: metrics.outbound_links.length,
        internal_links_count: metrics.internal_links.length,
        image_count: metrics.image_count,
        content_fetched_at: new Date().toISOString(),
        // M8: SRO base field (free — bez LLM). extracted_* zostana NULL, wypelni je przyszly LLM extraction.
        first_100_words: first100Words,
        // M9: pre-computed tokens dla matcher Level 2 (containment)
        title_tokens_normalized: titleTokens,
        first_100_tokens_normalized: first100Tokens,
        // quality reset — bedzie ponownie oceniane w assessQualityForWebsite
        quality_score: null,
        quality_assessed_at: null,
      };

      // Cast (supabase as any) — auto-gen types.ts nie zna pol M8/M9
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error: upsertError } = await (supabase as any)
        .from("wp_posts")
        .upsert(postData, { onConflict: "website_id,wp_post_id" });

      if (upsertError) {
        logError(PREFIX, `Upsert post ${rp.id}: ${upsertError.message}`);
        result.errors.push({ postId: rp.id, error: upsertError.message });
      } else {
        result.updated++;
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      logError(PREFIX, `Post ${rp.id} failed: ${msg}`);
      result.errors.push({ postId: rp.id, error: msg });
    }
  }

  logPublic(
    PREFIX,
    `Sync done: fetched=${result.fetched}, updated=${result.updated}, errors=${result.errors.length}`,
  );
  options.onLog?.(
    "stage",
    `  ✓ Sync zakończony: fetched=${result.fetched}, updated=${result.updated}, errors=${result.errors.length}`,
  );

  return result;
}

// =============================================
// assessQualityForWebsite
// =============================================

export interface QualityAssessResult {
  assessed: number;
  keep_count: number;
  borderline_count: number;
  refurbish_count: number;
}

export async function assessQualityForWebsite(
  websiteId: string,
  options: {
    onProgress?: (current: number, total: number) => void;
    onLog?: SyncOnLog;
    force?: boolean;
    useLlmCheck?: boolean; // placeholder — prompt prześle Darek, na razie ignorujemy
  } = {},
): Promise<QualityAssessResult> {
  const website = await loadWebsite(websiteId);
  if (!website) throw new Error("Website not found");

  const query = supabase
    .from("wp_posts")
    .select(
      "id, content, published_at, modified_at, meta_description, featured_image_url, quality_score",
    )
    .eq("website_id", websiteId)
    .not("content", "is", null);

  const { data, error } = await query;
  if (error) {
    logError(PREFIX, `Load wp_posts for quality: ${error.message}`);
    throw error;
  }

  type PostRow = {
    id: string;
    content: string | null;
    published_at: string | null;
    modified_at: string | null;
    meta_description: string | null;
    featured_image_url: string | null;
    quality_score: number | null;
  };

  const toAssess = (data as PostRow[]).filter((r) => options.force || r.quality_score === null);
  const total = toAssess.length;
  const breakdown = { keep: 0, borderline: 0, refurbish: 0 };
  let assessed = 0;

  logPublic(PREFIX, `Quality assess dla ${website.name}: ${total} postow do oceny`);
  options.onLog?.(
    "info",
    `  → Ocena jakości heurystyczna (6 reguł): word count, struktura H2/H3, keyword stuffing, linki, wiek, meta. ${total} wpisów do oceny.`,
  );

  // Checkpoints at 25/50/75%
  const checkpoints = new Set<number>([
    Math.floor(total * 0.25),
    Math.floor(total * 0.5),
    Math.floor(total * 0.75),
  ]);

  for (let i = 0; i < toAssess.length; i++) {
    const p = toAssess[i];
    const assessment = assessWpPostQuality(p, website.url);
    breakdown[assessment.recommendation]++;
    assessed++;

    const { error: updErr } = await supabase
      .from("wp_posts")
      .update({
        quality_score: assessment.score,
        quality_red_flags: assessment.red_flags,
        quality_recommendation: assessment.recommendation,
        quality_assessed_at: assessment.assessed_at,
        quality_method: assessment.method,
      })
      .eq("id", p.id);

    if (updErr) {
      logError(PREFIX, `Quality update post ${p.id}: ${updErr.message}`);
    }

    options.onProgress?.(i + 1, total);

    if (checkpoints.has(i + 1)) {
      const pct = Math.round(((i + 1) / total) * 100);
      options.onLog?.(
        "info",
        `  Quality progress ${pct}% (${i + 1}/${total}) — keep=${breakdown.keep} borderline=${breakdown.borderline} refurbish=${breakdown.refurbish}`,
      );
    }
  }

  logPublic(
    PREFIX,
    `Quality done: assessed=${assessed} keep=${breakdown.keep} borderline=${breakdown.borderline} refurbish=${breakdown.refurbish}`,
  );
  options.onLog?.(
    "stage",
    `  ✓ Quality done: keep=${breakdown.keep} borderline=${breakdown.borderline} refurbish=${breakdown.refurbish}`,
  );

  return {
    assessed,
    keep_count: breakdown.keep,
    borderline_count: breakdown.borderline,
    refurbish_count: breakdown.refurbish,
  };
}

// =============================================
// syncAndAssessForMap
// =============================================
//
// REFAKTOR 2026-04-19: krok 1 w UI ("Pobierz wpisy") robi teraz TYLKO:
//  Faza 1: fetch WP REST → wp_posts (+ analyzeWpHtml structural metrics)
//  Faza 2: Senuto SEO sync (non-blocking)
//
// Heurystyczna ocena jakosci (assessQualityForWebsite) zostala PRZENIESIONA
// do kroku 4 — wola sie ja tuz przed LLM audit jako baseline. Dzieki temu:
//  - krok 1 jest "tylko pobieranie" (jasne semantycznie)
//  - heurystyka + LLM lecą razem w kroku 4 (LLM i tak nadpisuje quality_recommendation)
//
// Pole `assessed` w wyniku = 0 (heurystyka odpala sie pozniej). `quality_breakdown`
// czytane z DB (moze pokazywac wartosci z poprzedniego audytu).

export interface SyncAndAssessMapResult {
  map_id: string;
  website_id: string;
  fetched: number;
  assessed: number;
  quality_breakdown: { keep: number; borderline: number; refurbish: number };
  seo_sync?: {
    posts_with_rankings: number;
    total_keywords: number;
    duration_ms: number;
  };
  seo_sync_failed?: boolean;
  seo_sync_error?: string;
}

export async function syncAndAssessForMap(
  mapId: string,
  options: {
    userId: string;
    onProgress?: (phase: "sync" | "quality", current: number, total: number) => void;
    onLog?: SyncOnLog;
    useLlmCheck?: boolean;
  },
): Promise<SyncAndAssessMapResult> {
  const { data: map, error } = await supabase
    .from("thematic_maps")
    .select("id, website_id, name")
    .eq("id", mapId)
    .maybeSingle();

  if (error || !map) {
    logError(PREFIX, `Mapa ${mapId} nie znaleziona`);
    throw new Error("Map not found");
  }
  const mapRow = map as { id: string; website_id: string | null; name: string };
  if (!mapRow.website_id) {
    throw new Error("Mapa nie ma przypisanej domeny WP (website_id)");
  }

  logPublic(PREFIX, `syncAndAssessForMap ${mapRow.name} → website ${mapRow.website_id}`);
  options.onLog?.("info", `▶ Faza 1/2: Sync wpisów z WordPress`);

  const syncRes = await syncWpPostsWithContent(mapRow.website_id, {
    userId: options.userId,
    onProgress: (current, total) => options.onProgress?.("sync", current, total),
    onLog: options.onLog,
  });

  // Update thematic_maps meta
  const finalCount =
    (await supabase
      .from("wp_posts")
      .select("id", { count: "exact", head: true })
      .eq("website_id", mapRow.website_id)).count ?? 0;

  await supabase
    .from("thematic_maps")
    .update({
      wp_posts_synced_at: new Date().toISOString(),
      wp_posts_count: finalCount,
      existing_posts_enabled: true,
    })
    .eq("id", mapRow.id);

  // Breakdown z DB — moze byc z poprzedniego audytu (jesli user juz odpalal krok 4).
  // Dla swiezego website: wszystko 0 dopoki user nie odpali "Audyt jakosci".
  const breakdown = await countQualityRecommendation(mapRow.website_id);

  // Faza 2 (opt-in auto): Senuto SEO sync. NIE blokuje WP sync gdy padnie.
  let seo_sync: SyncAndAssessMapResult["seo_sync"];
  let seo_sync_failed = false;
  let seo_sync_error: string | undefined;
  try {
    options.onLog?.("info", `▶ Faza 2/2: Sync danych SEO (Senuto)`);
    const { syncSeoDataForWebsite } = await import("./senutoSyncService");
    const senutoRes = await syncSeoDataForWebsite(mapRow.website_id, {
      onLog: (msg) => options.onLog?.("info", msg),
    });
    seo_sync = {
      posts_with_rankings: senutoRes.posts_with_rankings,
      total_keywords: senutoRes.total_keywords,
      duration_ms: senutoRes.duration_ms,
    };
    options.onLog?.(
      "success",
      `✓ Senuto: ${senutoRes.posts_with_rankings}/${senutoRes.total_posts} z rankingami, ${senutoRes.total_keywords} keys, ${senutoRes.total_cannibalization_pairs} kanibalizacji`,
    );
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    logError(PREFIX, `Senuto sync failed (non-blocking): ${msg}`);
    options.onLog?.("error", `⚠ Senuto sync padł (nieblokujące): ${msg}`);
    seo_sync_failed = true;
    seo_sync_error = msg;
  }

  return {
    map_id: mapRow.id,
    website_id: mapRow.website_id,
    fetched: syncRes.fetched,
    assessed: 0, // heurystyka przeniesiona do kroku 4 (Audyt jakosci)
    quality_breakdown: breakdown,
    seo_sync,
    seo_sync_failed,
    seo_sync_error,
  };
}
